import { Center } from '@televet/kibble-ui/build/chakra';
import React, { useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import { FeatureFlagName } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { useTracking } from 'shared/providers/TrackingProvider';
import { TasksEvents } from 'shared/providers/TrackingProvider/EventNames';
import { TaskTabsLayout } from './components/TaskTabsLayout';
import { TasksProvider } from './context/TasksProvider';
import useGenerateTaskLists from './hooks/useGenerateTaskLists';
import { useTopToolbarContext } from 'shared/components/Layouts/DefaultAuthenticated/components/TopToolbar/context/TopToolbarContext';
import LearnAboutTaskManager from './components/LearnAboutTaskManager';

const Tasks = (): JSX.Element => {
  const { isFeatureEnabled, isFeatureFlagsLoading } = useFeatureFlag();
  const { currentClinicId } = useClinicUser();
  const { loaded, taskLists } = useGenerateTaskLists();
  const { trackEvent } = useTracking();
  const { setTopToolbarAdditionalContent } = useTopToolbarContext();
  const taskIsLoadingOrFeatureIsNotEnabled =
    isFeatureFlagsLoading || !loaded || !isFeatureEnabled(FeatureFlagName.Tasks);

  useEffect(() => {
    if (taskIsLoadingOrFeatureIsNotEnabled) {
      return;
    }

    trackEvent(TasksEvents.PageViewed, {
      clinicId: currentClinicId,
    });
  }, [currentClinicId, trackEvent, taskIsLoadingOrFeatureIsNotEnabled]);

  useEffect(() => {
    if (!taskIsLoadingOrFeatureIsNotEnabled) {
      setTopToolbarAdditionalContent(<LearnAboutTaskManager />);
    }

    return (): void => {
      setTopToolbarAdditionalContent(undefined);
    };
  }, [setTopToolbarAdditionalContent, taskIsLoadingOrFeatureIsNotEnabled]);

  if (isFeatureFlagsLoading || !loaded) {
    return (
      <Center height="100%">
        <LoadingSpinner direction="row" />
      </Center>
    );
  }

  // Doing it here instead of in the route because it redirects to conversations while the page is loading and it's not a good experience
  if (!isFeatureEnabled(FeatureFlagName.Tasks)) {
    return <Redirect to="/" />;
  }

  return (
    <TasksProvider>
      <TaskTabsLayout taskLists={taskLists} />
    </TasksProvider>
  );
};

export default Tasks;
